import React, { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { useQueryClient } from 'react-query'
import { useAuthStore } from '@/store/authStore'
import { taskService } from '@/services/taskService'
import { Download, Trash2, Upload, Eye, X, FileText, File, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'

const formatBytes = (bytes) => {
  if (!bytes) return ''
  if (bytes < 1024) return `${bytes}B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`
}

// detected_type is sniffed from the file content by the server; older uploads only have mime_type
const kindOf = (att) => {
  if (att.detected_type) return att.detected_type
  if (att.mime_type?.startsWith('image/')) return 'image'
  if (att.mime_type?.includes('pdf')) return 'pdf'
  return 'other'
}
const isImage = (att) => ['png', 'jpeg', 'gif', 'webp', 'image'].includes(kindOf(att))
const isPdf = (att) => kindOf(att) === 'pdf'
const hasTextPreview = (att) => ['text', 'docx', 'xlsx', 'zip'].includes(att.detected_type)
  || /text\/|csv|json|wordprocessingml|spreadsheetml|zip/.test(att.mime_type || '')

// File type icon for non-image files
const FileIcon = ({ mimeType }) => {
  if (mimeType?.includes('pdf')) return <FileText className="h-8 w-8 text-red-500" />
  if (mimeType?.includes('word') || mimeType?.includes('document')) return <FileText className="h-8 w-8 text-blue-500" />
  if (mimeType?.includes('sheet') || mimeType?.includes('excel') || mimeType?.includes('csv')) return <FileText className="h-8 w-8 text-green-500" />
  return <File className="h-8 w-8 text-muted-foreground" />
}

// Text/Markdown extracted by the server (Word, Excel, text files, ZIP listings)
const TextPreview = ({ projectId, taskId, attachment }) => {
  const [state, setState] = useState({ loading: true })

  useEffect(() => {
    let cancelled = false
    taskService.getAttachmentText(projectId, taskId, attachment.id)
      .then(res => { if (!cancelled) setState({ loading: false, ...res.data }) })
      .catch(err => { if (!cancelled) setState({ loading: false, note: err.response?.data?.error || 'Preview failed' }) })
    return () => { cancelled = true }
  }, [attachment.id])

  if (state.loading) {
    return <div className="flex items-center gap-2 p-10 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading preview…</div>
  }
  return (
    <div className="p-4">
      {state.note && <p className="text-xs text-muted-foreground mb-2">{state.note}</p>}
      {state.text ? (
        <pre className="text-xs font-mono whitespace-pre-wrap break-words max-h-[70vh] overflow-auto">{state.text}</pre>
      ) : !state.note && <p className="text-sm text-muted-foreground">Nothing to preview.</p>}
      {state.truncated && <p className="text-xs text-muted-foreground mt-2">Preview truncated — download the file to see everything.</p>}
    </div>
  )
}

// Preview modal
const PreviewModal = ({ attachment, projectId, taskId, onClose }) => {
  if (!attachment) return null
  const src = attachment.preview_url

  let body
  if (isImage(attachment) && src) {
    body = (
      <img
        src={src}
        alt={attachment.original_filename}
        className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg"
      />
    )
  } else if (isPdf(attachment) && src) {
    body = <iframe src={src} title={attachment.original_filename} className="w-[min(90vw,1000px)] h-[85vh] rounded-lg bg-white" />
  } else if (hasTextPreview(attachment)) {
    body = (
      <div className="bg-background rounded-lg w-[min(90vw,900px)] overflow-hidden">
        <TextPreview projectId={projectId} taskId={taskId} attachment={attachment} />
      </div>
    )
  } else {
    body = (
      <div className="bg-background rounded-lg p-10 text-center">
        <FileIcon mimeType={attachment.mime_type} />
        <p className="mt-4 text-sm">{attachment.original_filename}</p>
        <p className="text-xs text-muted-foreground mt-1">Preview not available for this file type</p>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" onClick={onClose}>
      <div className="relative max-w-[90vw] max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
        <Button
          variant="ghost" size="icon"
          className="absolute -top-10 right-0 text-white hover:bg-white/20 h-8 w-8"
          onClick={onClose}
        >
          <X className="h-5 w-5" />
        </Button>
        {body}
        <p className="text-center text-white/80 text-sm mt-3">{attachment.original_filename}</p>
      </div>
    </div>
  )
}

const TaskAttachments = ({ taskId, projectId, attachments = [] }) => {
  const { user } = useAuthStore()
  const queryClient = useQueryClient()
  const inputRef = useRef(null)
  const [previewAtt, setPreviewAtt] = useState(null)

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || [])
    if (!files.length) return
    try {
      await taskService.uploadAttachments(projectId, taskId, files)
      queryClient.invalidateQueries(['projects', String(projectId), 'tasks', String(taskId)])
      toast.success(`${files.length} file(s) uploaded`)
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed')
    } finally {
      e.target.value = ''
    }
  }

  const handleDelete = async (attachment) => {
    try {
      await taskService.deleteAttachment(projectId, taskId, attachment.id)
      queryClient.invalidateQueries(['projects', String(projectId), 'tasks', String(taskId)])
      toast.success('Attachment removed')
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete')
    }
  }

  // download_url is a short-lived signed link that answers with Content-Disposition: attachment
  const handleDownload = (attachment) => {
    if (!attachment.download_url) return toast.error('Download link unavailable — reload the page')
    const link = document.createElement('a')
    link.href = attachment.download_url
    link.rel = 'noopener'
    document.body.appendChild(link)
    link.click()
    link.remove()
  }

  return (
    <div className="space-y-3">
      {/* Attachment grid */}
      {attachments.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {attachments.map(att => {
            const imgSrc = isImage(att) ? att.preview_url : null

            return (
              <div
                key={att.id}
                className="group relative border rounded-lg overflow-hidden bg-muted/30 hover:border-primary/30 transition-colors"
              >
                {/* Thumbnail / File icon */}
                <div className="aspect-[4/3] flex items-center justify-center bg-muted/50 overflow-hidden">
                  {imgSrc ? (
                    <img
                      src={imgSrc}
                      alt={att.original_filename}
                      loading="lazy"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <FileIcon mimeType={att.mime_type} />
                  )}
                </div>

                {/* Hover actions overlay */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100">
                  <Button
                    variant="secondary" size="icon"
                    className="h-8 w-8 bg-white/90 hover:bg-white text-foreground"
                    title="Preview"
                    onClick={() => setPreviewAtt(att)}
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="secondary" size="icon"
                    className="h-8 w-8 bg-white/90 hover:bg-white text-foreground"
                    title="Download"
                    onClick={() => handleDownload(att)}
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                  {att.uploaded_by === user?.id && (
                    <Button
                      variant="secondary" size="icon"
                      className="h-8 w-8 bg-white/90 hover:bg-white text-destructive hover:text-destructive"
                      title="Delete"
                      onClick={() => handleDelete(att)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>

                {/* File info */}
                <div className="p-2">
                  <p className="text-xs font-medium truncate" title={att.original_filename}>
                    {att.original_filename}
                  </p>
                  {att.file_size && (
                    <p className="text-[10px] text-muted-foreground mt-0.5">{formatBytes(att.file_size)}</p>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Upload button */}
      <input ref={inputRef} type="file" multiple className="hidden" onChange={handleUpload} />
      <Button variant="outline" size="sm" className="w-full" onClick={() => inputRef.current?.click()}>
        <Upload className="h-3.5 w-3.5 mr-1.5" />
        Upload files
      </Button>

      {/* Preview modal */}
      {previewAtt && (
        <PreviewModal attachment={previewAtt} projectId={projectId} taskId={taskId} onClose={() => setPreviewAtt(null)} />
      )}
    </div>
  )
}

export default TaskAttachments
